import { 
  IProgressRepository,
  CreateLessonProgressData,
  UpdateLessonProgressData,
  GetLessonProgressData,
  CreateQuizProgressData,
  UpdateQuizProgressData,
  GetQuizProgressData,
  CreateModuleProgressData,
  UpdateModuleProgressData,
  GetModuleProgressData,
  CreateCourseProgressData,
  UpdateCourseProgressData,
  GetCourseProgressData,
  QuizAnswerData
} from '../repository/progress.repository';

export interface IProgressUseCase {
  // Lesson Progress
  startLesson(userId: string, courseId: string, moduleId: string, lessonId: string): Promise<GetLessonProgressData>;
  updateLessonProgress(userId: string, lessonId: string, progressData: {
    completionPercentage?: number;
    timeSpentSeconds?: number;
    videoProgressSeconds?: number;
    notes?: string;
    bookmarked?: boolean;
  }): Promise<GetLessonProgressData>;
  completeLesson(userId: string, lessonId: string, timeSpentSeconds: number): Promise<GetLessonProgressData>;

  // Quiz Progress
  startQuiz(userId: string, courseId: string, quizId: string, moduleId?: string, lessonId?: string): Promise<GetQuizProgressData>;
  submitQuizAnswer(userId: string, quizId: string, questionId: string, answer: string | string[], timeSpentSeconds: number): Promise<GetQuizProgressData>;
  completeQuiz(userId: string, quizId: string, answers: QuizAnswerData[], totalTimeSeconds: number): Promise<GetQuizProgressData>;

  // Aggregate Progress Calculations
  calculateModuleProgress(userId: string, moduleId: string): Promise<GetModuleProgressData>;
  calculateCourseProgress(userId: string, courseId: string): Promise<GetCourseProgressData>;

  // Progress Queries
  getUserProgressSummary(userId: string, courseId?: string): Promise<{
    courseProgress: GetCourseProgressData[];
    moduleProgress: GetModuleProgressData[];
    lessonProgress: GetLessonProgressData[];
    quizProgress: GetQuizProgressData[];
    overallStats: {
      totalCoursesStarted: number;
      totalCoursesCompleted: number;
      totalLessonsCompleted: number;
      totalQuizzesCompleted: number;
      averageQuizScore: number;
      totalTimeSpentHours: number;
    };
  }>;

  // Learning Analytics
  getLearningAnalytics(userId: string, courseId: string): Promise<{
    dailyActivity: { date: string; lessonsCompleted: number; timeSpentMinutes: number; }[];
    weakAreas: { topic: string; averageScore: number; attemptsCount: number; }[];
    strongAreas: { topic: string; averageScore: number; attemptsCount: number; }[];
    learningVelocity: { week: string; completionRate: number; }[];
    recommendedActions: string[];
  }>;
}

export class ProgressUseCase implements IProgressUseCase {
  constructor(
    private progressRepo: IProgressRepository
  ) {
    console.log('[ProgressUseCase] Constructor - Progress repository initialized');
  }

  // ===== LESSON PROGRESS =====

  async startLesson(userId: string, courseId: string, moduleId: string, lessonId: string): Promise<GetLessonProgressData> {
    console.log('[ProgressUseCase.startLesson] START - User:', userId, 'Lesson:', lessonId);
    
    try {
      // Check if progress already exists
      const existingProgress = await this.progressRepo.getLessonProgressByUser(userId, lessonId);
      
      if (existingProgress) {
        console.log('[ProgressUseCase.startLesson] Updating existing progress');
        // Update last accessed time
        return await this.progressRepo.updateLessonProgress(existingProgress.id, {
          lastAccessedAt: new Date(),
          status: existingProgress.status === 'notStarted' ? 'inProgress' : existingProgress.status
        });
      } else {
        console.log('[ProgressUseCase.startLesson] Creating new progress');
        // Create new progress record
        const progressData: CreateLessonProgressData = {
          userId: userId,
          courseId: courseId,
          moduleId: moduleId,
          lessonId: lessonId,
          status: 'inProgress',
          completionPercentage: 0,
          timeSpentSeconds: 0,
          startedAt: new Date(),
          lastAccessedAt: new Date()
        };

        return await this.progressRepo.createLessonProgress(progressData);
      }
    } catch (error) {
      console.error('[ProgressUseCase.startLesson] ERROR:', error);
      throw error;
    }
  }

  async updateLessonProgress(userId: string, lessonId: string, progressData: {
    completionPercentage?: number;
    timeSpentSeconds?: number;
    videoProgressSeconds?: number;
    notes?: string;
    bookmarked?: boolean;
  }): Promise<GetLessonProgressData> {
    console.log('[ProgressUseCase.updateLessonProgress] START - User:', userId, 'Lesson:', lessonId, 'Data:', progressData);
    
    try {
      const existingProgress = await this.progressRepo.getLessonProgressByUser(userId, lessonId);
      
      if (!existingProgress) {
        throw new Error('Lesson progress not found. Please start the lesson first.');
      }

      // Determine status based on completion percentage
      let status = existingProgress.status;
      if (progressData.completionPercentage !== undefined) {
        if (progressData.completionPercentage >= 100) {
          status = 'completed';
        } else if (progressData.completionPercentage > 0) {
          status = 'inProgress';
        }
      }

      const updateData: UpdateLessonProgressData = {
        ...progressData,
        status,
        lastAccessedAt: new Date(),
        ...(status === 'completed' && !existingProgress.completedAt ? { completedAt: new Date() } : {})
      };

      const updatedProgress = await this.progressRepo.updateLessonProgress(existingProgress.id, updateData);
      
      // If lesson was completed, update module progress
      if (status === 'completed' && existingProgress.status !== 'completed') {
        await this.calculateModuleProgress(userId, existingProgress.moduleId);
      }

      return updatedProgress;
    } catch (error) {
      console.error('[ProgressUseCase.updateLessonProgress] ERROR:', error);
      throw error;
    }
  }

  async completeLesson(userId: string, lessonId: string, timeSpentSeconds: number): Promise<GetLessonProgressData> {
    console.log('[ProgressUseCase.completeLesson] START - User:', userId, 'Lesson:', lessonId, 'Time:', timeSpentSeconds);
    
    try {
      return await this.updateLessonProgress(userId, lessonId, {
        completionPercentage: 100,
        timeSpentSeconds: timeSpentSeconds
      });
    } catch (error) {
      console.error('[ProgressUseCase.completeLesson] ERROR:', error);
      throw error;
    }
  }

  // ===== QUIZ PROGRESS =====

  async submitQuizAttempt(userId: string, attemptData: any): Promise<GetQuizProgressData> {
    console.log('[ProgressUseCase.submitQuizAttempt] START - User:', userId, 'Quiz:', attemptData.quizId);
    
    try {
      // Create quiz progress record
      const progressData = {
        userId: userId,
        quizId: attemptData.quizId,
        courseId: attemptData.courseId,
        moduleId: attemptData.moduleId,
        score: attemptData.score,
        maxScore: attemptData.maxScore,
        percentage: attemptData.percentage,
        passed: attemptData.passed,
        timeSpentSeconds: attemptData.timeSpentSeconds,
        answers: attemptData.answers,
        startedAt: attemptData.startedAt,
        completedAt: attemptData.completedAt,
        attemptNumber: attemptData.attemptNumber
      };

      const quizProgress = await this.progressRepository.createQuizProgress(progressData);

      console.log('[ProgressUseCase.submitQuizAttempt] SUCCESS - Quiz submitted, passed:', attemptData.passed);
      return quizProgress;
    } catch (error) {
      console.error('[ProgressUseCase.submitQuizAttempt] ERROR:', error);
      throw error;
    }
  }

  async getQuizAttempts(userId: string, quizId: string): Promise<any[]> {
    console.log('[ProgressUseCase.getQuizAttempts] START - User:', userId, 'Quiz:', quizId);
    
    try {
      // Get all quiz progress records for this user and quiz
      const allProgress = await this.progressRepository.getUserQuizProgress(userId);
      const quizAttempts = allProgress.filter(progress => progress.quizId === quizId);

      console.log('[ProgressUseCase.getQuizAttempts] SUCCESS - Found:', quizAttempts.length, 'attempts');
      return quizAttempts;
    } catch (error) {
      console.error('[ProgressUseCase.getQuizAttempts] ERROR:', error);
      throw error;
    }
  }

  async startQuiz(userId: string, courseId: string, quizId: string, moduleId?: string, lessonId?: string): Promise<GetQuizProgressData> {
    console.log('[ProgressUseCase.startQuiz] START - User:', userId, 'Quiz:', quizId);
    
    try {
      // Get existing attempts to determine attempt number
      const existingAttempts = await this.progressRepo.getQuizProgressByUser(userId, quizId);
      const attemptNumber = existingAttempts.length + 1;

      // Create new quiz attempt
      const progressData: CreateQuizProgressData = {
        userId: userId,
        courseId: courseId,
        moduleId: moduleId,
        lessonId: lessonId,
        quizId: quizId,
        attemptNumber: attemptNumber,
        status: 'inProgress',
        maxScore: 100, // This should come from quiz configuration
        passingScore: 70, // This should come from quiz configuration
        questionsAnswered: 0,
        totalQuestions: 10, // This should come from quiz configuration
        timeSpentSeconds: 0,
        startedAt: new Date(),
        answers: [],
        passed: false
      };

      return await this.progressRepo.createQuizProgress(progressData);
    } catch (error) {
      console.error('[ProgressUseCase.startQuiz] ERROR:', error);
      throw error;
    }
  }

  async submitQuizAnswer(userId: string, quizId: string, questionId: string, answer: string | string[], timeSpentSeconds: number): Promise<GetQuizProgressData> {
    console.log('[ProgressUseCase.submitQuizAnswer] START - User:', userId, 'Quiz:', quizId, 'Question:', questionId);
    
    try {
      // Get current quiz progress (latest attempt)
      const attempts = await this.progressRepo.getQuizProgressByUser(userId, quizId);
      const currentAttempt = attempts[0]; // Most recent attempt

      if (!currentAttempt || currentAttempt.status !== 'inProgress') {
        throw new Error('No active quiz attempt found');
      }

      // This would typically validate the answer against correct answers
      // For now, we'll simulate answer validation
      const isCorrect = this.validateQuizAnswer(questionId, answer);
      const pointsEarned = isCorrect ? 10 : 0; // This should come from question configuration

      const answerData: QuizAnswerData = {
        questionId: questionId,
        questionText: `Question ${questionId}`, // This should come from question data
        userAnswer: answer,
        correctAnswer: 'sampleCorrectAnswer', // This should come from question data
        isCorrect: isCorrect,
        pointsEarned: pointsEarned,
        maxPoints: 10, // This should come from question configuration
        timeSpentSeconds: timeSpentSeconds
      };

      // Update answers array
      const updatedAnswers = [...currentAttempt.answers, answerData];
      const questionsAnswered = updatedAnswers.length;
      const currentScore = updatedAnswers.reduce((sum, ans) => sum + ans.pointsEarned, 0);

      const updateData: UpdateQuizProgressData = {
        answers: updatedAnswers,
        questionsAnswered: questionsAnswered,
        score: currentScore,
        timeSpentSeconds: currentAttempt.timeSpentSeconds + timeSpentSeconds
      };

      return await this.progressRepo.updateQuizProgress(currentAttempt.id, updateData);
    } catch (error) {
      console.error('[ProgressUseCase.submitQuizAnswer] ERROR:', error);
      throw error;
    }
  }

  async completeQuiz(userId: string, quizId: string, answers: QuizAnswerData[], totalTimeSeconds: number): Promise<GetQuizProgressData> {
    console.log('[ProgressUseCase.completeQuiz] START - User:', userId, 'Quiz:', quizId);
    
    try {
      // Get current quiz progress (latest attempt)
      const attempts = await this.progressRepo.getQuizProgressByUser(userId, quizId);
      const currentAttempt = attempts[0];

      if (!currentAttempt) {
        throw new Error('No quiz attempt found');
      }

      // Calculate final score
      const totalScore = answers.reduce((sum, answer) => sum + answer.pointsEarned, 0);
      const passed = totalScore >= currentAttempt.passingScore;
      const status = passed ? 'completed' : 'failed';

      const updateData: UpdateQuizProgressData = {
        status,
        score: totalScore,
        questionsAnswered: answers.length,
        timeSpentSeconds: totalTimeSeconds,
        answers,
        passed,
        completedAt: new Date()
      };

      const updatedProgress = await this.progressRepo.updateQuizProgress(currentAttempt.id, updateData);

      // If quiz was completed successfully, update module and course progress
      if (passed) {
        if (currentAttempt.moduleId) {
          await this.calculateModuleProgress(userId, currentAttempt.moduleId);
        }
        await this.calculateCourseProgress(userId, currentAttempt.courseId);
      }

      return updatedProgress;
    } catch (error) {
      console.error('[ProgressUseCase.completeQuiz] ERROR:', error);
      throw error;
    }
  }

  // ===== PROGRESS CALCULATIONS =====

  async calculateModuleProgress(userId: string, moduleId: string): Promise<GetModuleProgressData> {
    console.log('[ProgressUseCase.calculateModuleProgress] START - User:', userId, 'Module:', moduleId);
    
    try {
      // Get all lesson progress for this module
      const allLessonProgress = await this.progressRepo.getUserLessonProgress(userId);
      const moduleLessonProgress = allLessonProgress.filter(p => p.moduleId === moduleId);

      // Get all quiz progress for this module
      const allQuizProgress = await this.progressRepo.getUserQuizProgress(userId);
      const moduleQuizProgress = allQuizProgress.filter(p => p.moduleId === moduleId);

      // Calculate module statistics
      const completedLessons = moduleLessonProgress.filter(p => p.status === 'completed').length;
      const totalLessons = moduleLessonProgress.length;
      const completedQuizzes = moduleQuizProgress.filter(p => p.passed).length;
      const totalQuizzes = moduleQuizProgress.length;
      
      const totalTimeSpent = [
        ...moduleLessonProgress.map(p => p.timeSpentSeconds),
        ...moduleQuizProgress.map(p => p.timeSpentSeconds)
      ].reduce((sum, time) => sum + time, 0);

      // Calculate completion percentage
      const totalItems = totalLessons + totalQuizzes;
      const completedItems = completedLessons + completedQuizzes;
      const completionPercentage = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

      // Determine status
      let status: 'notStarted' | 'inProgress' | 'completed' = 'notStarted';
      if (completionPercentage === 100) {
        status = 'completed';
      } else if (completionPercentage > 0) {
        status = 'inProgress';
      }

      // Check if module progress already exists
      const existingProgress = await this.progressRepo.getModuleProgressByUser(userId, moduleId);

      if (existingProgress) {
        // Update existing progress
        const updateData: UpdateModuleProgressData = {
          status,
          completionPercentage: completionPercentage,
          lessonsCompleted: completedLessons,
          quizzesCompleted: completedQuizzes,
          timeSpentSeconds: totalTimeSpent,
          lastAccessedAt: new Date(),
          ...(status === 'completed' && !existingProgress.completedAt ? { completedAt: new Date() } : {})
        };

        const updatedProgress = await this.progressRepo.updateModuleProgress(existingProgress.id, updateData);
        
        // Update course progress
        await this.calculateCourseProgress(userId, existingProgress.courseId);
        
        return updatedProgress;
      } else {
        // Create new module progress
        const courseId = moduleLessonProgress[0]?.courseId || moduleQuizProgress[0]?.courseId;
        if (!courseId) {
          throw new Error('Could not determine course ID for module progress');
        }

        const progressData: CreateModuleProgressData = {
          userId: userId,
          courseId: courseId,
          moduleId: moduleId,
          status,
          completionPercentage: completionPercentage,
          lessonsCompleted: completedLessons,
          totalLessons: totalLessons,
          quizzesCompleted: completedQuizzes,
          totalQuizzes: totalQuizzes,
          timeSpentSeconds: totalTimeSpent,
          lastAccessedAt: new Date(),
          ...(status === 'completed' ? { completedAt: new Date() } : {})
        };

        const newProgress = await this.progressRepo.createModuleProgress(progressData);
        
        // Update course progress
        await this.calculateCourseProgress(userId, courseId);
        
        return newProgress;
      }
    } catch (error) {
      console.error('[ProgressUseCase.calculateModuleProgress] ERROR:', error);
      throw error;
    }
  }

  async calculateCourseProgress(userId: string, courseId: string): Promise<GetCourseProgressData> {
    console.log('[ProgressUseCase.calculateCourseProgress] START - User:', userId, 'Course:', courseId);
    
    try {
      // Get all progress data for this course
      const moduleProgress = await this.progressRepo.getUserModuleProgress(userId, courseId);
      const lessonProgress = await this.progressRepo.getUserLessonProgress(userId, courseId);
      const quizProgress = await this.progressRepo.getUserQuizProgress(userId, courseId);

      // Calculate course statistics
      const completedModules = moduleProgress.filter(p => p.status === 'completed').length;
      const totalModules = moduleProgress.length;
      const completedLessons = lessonProgress.filter(p => p.status === 'completed').length;
      const totalLessons = lessonProgress.length;
      const completedQuizzes = quizProgress.filter(p => p.passed).length;
      const totalQuizzes = quizProgress.length;

      const totalTimeSpent = [
        ...lessonProgress.map(p => p.timeSpentSeconds),
        ...quizProgress.map(p => p.timeSpentSeconds)
      ].reduce((sum, time) => sum + time, 0);

      // Calculate average quiz score
      const passedQuizzes = quizProgress.filter(p => p.passed && p.score !== undefined);
      const averageQuizScore = passedQuizzes.length > 0 
        ? passedQuizzes.reduce((sum, quiz) => sum + (quiz.score || 0), 0) / passedQuizzes.length 
        : undefined;

      // Calculate overall completion percentage
      const totalItems = totalLessons + totalQuizzes;
      const completedItems = completedLessons + completedQuizzes;
      const completionPercentage = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

      // Determine status
      let status: 'notStarted' | 'inProgress' | 'completed' = 'notStarted';
      if (completionPercentage === 100) {
        status = 'completed';
      } else if (completionPercentage > 0) {
        status = 'inProgress';
      }

      // Check if course progress already exists
      const existingProgress = await this.progressRepo.getCourseProgressByUser(userId, courseId);

      if (existingProgress) {
        // Update existing progress
        const updateData: UpdateCourseProgressData = {
          status,
          completionPercentage: completionPercentage,
          modulesCompleted: completedModules,
          lessonsCompleted: completedLessons,
          quizzesCompleted: completedQuizzes,
          totalTimeSpentSeconds: totalTimeSpent,
          averageQuizScore: averageQuizScore,
          lastAccessedAt: new Date(),
          ...(status === 'completed' && !existingProgress.completedAt ? { completedAt: new Date() } : {})
        };

        return await this.progressRepo.updateCourseProgress(existingProgress.id, updateData);
      } else {
        // Create new course progress
        const progressData: CreateCourseProgressData = {
          userId: userId,
          courseId: courseId,
          status,
          completionPercentage: completionPercentage,
          modulesCompleted: completedModules,
          totalModules: totalModules,
          lessonsCompleted: completedLessons,
          totalLessons: totalLessons,
          quizzesCompleted: completedQuizzes,
          totalQuizzes: totalQuizzes,
          totalTimeSpentSeconds: totalTimeSpent,
          averageQuizScore: averageQuizScore,
          lastAccessedAt: new Date(),
          ...(status === 'completed' ? { completedAt: new Date() } : {})
        };

        return await this.progressRepo.createCourseProgress(progressData);
      }
    } catch (error) {
      console.error('[ProgressUseCase.calculateCourseProgress] ERROR:', error);
      throw error;
    }
  }

  // ===== PROGRESS QUERIES =====

  async getUserProgressSummary(userId: string, courseId?: string): Promise<{
    courseProgress: GetCourseProgressData[];
    moduleProgress: GetModuleProgressData[];
    lessonProgress: GetLessonProgressData[];
    quizProgress: GetQuizProgressData[];
    overallStats: {
      totalCoursesStarted: number;
      totalCoursesCompleted: number;
      totalLessonsCompleted: number;
      totalQuizzesCompleted: number;
      averageQuizScore: number;
      totalTimeSpentHours: number;
    };
  }> {
    console.log('[ProgressUseCase.getUserProgressSummary] START - User:', userId, 'Course:', courseId);
    
    try {
      // Get all progress data
      // Independent reads: run them together rather than one after another.
      const [courseProgress, moduleProgress, lessonProgress, quizProgress] = await Promise.all([
        courseId
          ? this.progressRepo.getCourseProgressByUser(userId, courseId).then(p => p ? [p] : [])
          : this.progressRepo.getUserCourseProgress(userId),
        this.progressRepo.getUserModuleProgress(userId, courseId),
        this.progressRepo.getUserLessonProgress(userId, courseId),
        this.progressRepo.getUserQuizProgress(userId, courseId),
      ]);

      // Calculate overall statistics
      const totalCoursesStarted = courseProgress.length;
      const totalCoursesCompleted = courseProgress.filter(p => p.status === 'completed').length;
      const totalLessonsCompleted = lessonProgress.filter(p => p.status === 'completed').length;
      const totalQuizzesCompleted = quizProgress.filter(p => p.passed).length;

      const passedQuizzes = quizProgress.filter(p => p.passed && p.score !== undefined);
      const averageQuizScore = passedQuizzes.length > 0 
        ? passedQuizzes.reduce((sum, quiz) => sum + (quiz.score || 0), 0) / passedQuizzes.length 
        : 0;

      const totalTimeSpentSeconds = [
        ...courseProgress.map(p => p.totalTimeSpentSeconds),
        ...lessonProgress.map(p => p.timeSpentSeconds),
        ...quizProgress.map(p => p.timeSpentSeconds)
      ].reduce((sum, time) => sum + time, 0);

      const totalTimeSpentHours = Math.round((totalTimeSpentSeconds / 3600) * 100) / 100;

      return {
        courseProgress,
        moduleProgress,
        lessonProgress,
        quizProgress,
        overallStats: {
          totalCoursesStarted,
          totalCoursesCompleted,
          totalLessonsCompleted,
          totalQuizzesCompleted,
          averageQuizScore: Math.round(averageQuizScore * 100) / 100,
          totalTimeSpentHours
        }
      };
    } catch (error) {
      console.error('[ProgressUseCase.getUserProgressSummary] ERROR:', error);
      throw error;
    }
  }

  // ===== LEARNING ANALYTICS =====

  async getLearningAnalytics(userId: string, courseId: string): Promise<{
    dailyActivity: { date: string; lessonsCompleted: number; timeSpentMinutes: number; }[];
    weakAreas: { topic: string; averageScore: number; attemptsCount: number; }[];
    strongAreas: { topic: string; averageScore: number; attemptsCount: number; }[];
    learningVelocity: { week: string; completionRate: number; }[];
    recommendedActions: string[];
  }> {
    console.log('[ProgressUseCase.getLearningAnalytics] START - User:', userId, 'Course:', courseId);
    
    try {
      const lessonProgress = await this.progressRepo.getUserLessonProgress(userId, courseId);
      const quizProgress = await this.progressRepo.getUserQuizProgress(userId, courseId);

      // Calculate daily activity (last 30 days)
      const dailyActivity = this.calculateDailyActivity(lessonProgress, quizProgress);
      
      // Analyze quiz performance for weak/strong areas
      const { weakAreas, strongAreas } = this.analyzeQuizPerformance(quizProgress);
      
      // Calculate learning velocity (weekly completion rates)
      const learningVelocity = this.calculateLearningVelocity(lessonProgress, quizProgress);
      
      // Generate recommended actions
      const recommendedActions = this.generateRecommendedActions(lessonProgress, quizProgress, weakAreas);

      return {
        dailyActivity,
        weakAreas,
        strongAreas,
        learningVelocity,
        recommendedActions
      };
    } catch (error) {
      console.error('[ProgressUseCase.getLearningAnalytics] ERROR:', error);
      throw error;
    }
  }

  // ===== PRIVATE HELPER METHODS =====

  private validateQuizAnswer(questionId: string, answer: string | string[]): boolean {
    // This is a simplified validation - in reality, you'd check against stored correct answers
    return Math.random() > 0.3; // 70% chance of correct answer for demo
  }

  private calculateDailyActivity(lessonProgress: GetLessonProgressData[], quizProgress: GetQuizProgressData[]): 
    { date: string; lessonsCompleted: number; timeSpentMinutes: number; }[] {
    
    const dailyStats = new Map<string, { lessonsCompleted: number; timeSpentMinutes: number; }>();
    
    // Process lesson completions
    lessonProgress.filter(p => p.completedAt).forEach(lesson => {
      const date = lesson.completedAt!.toDate().toISOString().split('T')[0];
      const existing = dailyStats.get(date) || { lessonsCompleted: 0, timeSpentMinutes: 0 };
      dailyStats.set(date, {
        lessonsCompleted: existing.lessonsCompleted + 1,
        timeSpentMinutes: existing.timeSpentMinutes + Math.round(lesson.timeSpentSeconds / 60)
      });
    });

    // Process quiz completions
    quizProgress.filter(p => p.completedAt).forEach(quiz => {
      const date = quiz.completedAt!.toDate().toISOString().split('T')[0];
      const existing = dailyStats.get(date) || { lessonsCompleted: 0, timeSpentMinutes: 0 };
      dailyStats.set(date, {
        ...existing,
        timeSpentMinutes: existing.timeSpentMinutes + Math.round(quiz.timeSpentSeconds / 60)
      });
    });

    return Array.from(dailyStats.entries()).map(([date, stats]) => ({
      date,
      ...stats
    })).sort((a, b) => a.date.localeCompare(b.date));
  }

  private analyzeQuizPerformance(quizProgress: GetQuizProgressData[]): {
    weakAreas: { topic: string; averageScore: number; attemptsCount: number; }[];
    strongAreas: { topic: string; averageScore: number; attemptsCount: number; }[];
  } {
    // This is simplified - in reality, you'd analyze by topic/category
    const completedQuizzes = quizProgress.filter(p => p.score !== undefined);
    
    if (completedQuizzes.length === 0) {
      return { weakAreas: [], strongAreas: [] };
    }

    const averageScore = completedQuizzes.reduce((sum, quiz) => sum + (quiz.score || 0), 0) / completedQuizzes.length;
    
    const weakAreas = averageScore < 70 ? [
      { topic: 'Quiz Performance', averageScore: Math.round(averageScore), attemptsCount: completedQuizzes.length }
    ] : [];

    const strongAreas = averageScore >= 80 ? [
      { topic: 'Quiz Performance', averageScore: Math.round(averageScore), attemptsCount: completedQuizzes.length }
    ] : [];

    return { weakAreas, strongAreas };
  }

  private calculateLearningVelocity(lessonProgress: GetLessonProgressData[], quizProgress: GetQuizProgressData[]): 
    { week: string; completionRate: number; }[] {
    
    // Simplified weekly completion rate calculation
    const weeklyData = new Map<string, number>();
    
    [...lessonProgress, ...quizProgress].forEach(progress => {
      if (progress.completedAt) {
        const date = progress.completedAt.toDate();
        const weekStart = new Date(date.getFullYear(), date.getMonth(), date.getDate() - date.getDay());
        const weekKey = weekStart.toISOString().split('T')[0];
        weeklyData.set(weekKey, (weeklyData.get(weekKey) || 0) + 1);
      }
    });

    return Array.from(weeklyData.entries()).map(([week, completions]) => ({
      week,
      completionRate: completions
    })).sort((a, b) => a.week.localeCompare(b.week));
  }

  private generateRecommendedActions(
    lessonProgress: GetLessonProgressData[], 
    quizProgress: GetQuizProgressData[], 
    weakAreas: { topic: string; averageScore: number; attemptsCount: number; }[]
  ): string[] {
    const recommendations: string[] = [];

    // Check for incomplete lessons
    const incompleteLessons = lessonProgress.filter(p => p.status !== 'completed').length;
    if (incompleteLessons > 0) {
      recommendations.push(`Complete ${incompleteLessons} remaining lesson${incompleteLessons > 1 ? 's' : ''}`);
    }

    // Check for failed quizzes
    const failedQuizzes = quizProgress.filter(p => !p.passed).length;
    if (failedQuizzes > 0) {
      recommendations.push(`Retake ${failedQuizzes} failed quiz${failedQuizzes > 1 ? 'zes' : ''}`);
    }

    // Check for weak areas
    if (weakAreas.length > 0) {
      recommendations.push('Review materials for topics with low quiz scores');
    }

    // Check for recent activity
    const recentActivity = lessonProgress.filter(p => {
      const daysSinceAccess = (Date.now() - p.lastAccessedAt.getTime()) / (1000 * 60 * 60 * 24);
      return daysSinceAccess <= 7;
    }).length;

    if (recentActivity === 0) {
      recommendations.push('Resume learning - you haven\'t accessed any lessons this week');
    }

    return recommendations.length > 0 ? recommendations : ['Keep up the great work! Continue with your current pace.'];
  }
}
